import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BlockNote } from 'src/config/api/models/BlockNote';
import { StudentGroup } from 'src/config/api/models/Student';
import apiRoutesV2, { baseURL } from 'src/config/api/routes';
import { RootState, store } from 'src/modules/shared/store';

type getNotesParams = {
  pageId?: string;
  bookId?: string;
  studentId?: string;
  trajectoryId?: string;
  group?: Record<string, any>;
};

type createNoteParams = {
  pageId?: string;
  blockId?: string;
  content?: string;
};

type deleteNoteParams = {
  studentNoteId?: string;
};

type editNoteParams = deleteNoteParams & {
  content?: string;
};

export const notesApi = createApi({
  reducerPath: 'notesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getBlockNotes: builder.query<BlockNote[], getNotesParams>({
      query: ({ pageId }: getNotesParams) => ({
        url: apiRoutesV2.studentNotesByPageId(pageId as string),
        method: 'GET',
      }),
      transformResponse: (response: { data: BlockNote[] }) => {
        return response.data.map((note) => note);
      },
    }),
    createBlockNote: builder.mutation<BlockNote, createNoteParams>({
      query: ({ pageId, blockId, content }: createNoteParams) => ({
        url: apiRoutesV2.studentNotesByPageId(pageId as string),
        method: 'POST',
        body: { blockId, content },
      }),
    }),
    deleteBlockNote: builder.mutation({
      query: ({ studentNoteId }: deleteNoteParams) => ({
        url: apiRoutesV2.deleteStudentNote(studentNoteId as string),
        method: 'DELETE',
      }),
    }),
    patchBlockNote: builder.mutation({
      query: ({ content, studentNoteId }: editNoteParams) => ({
        url: apiRoutesV2.updateStudentNote(studentNoteId),
        method: 'PATCH',
        body: {
          content,
        },
      }),
    }),
    getNotesByBook: builder.query<StudentGroup[], getNotesParams>({
      query: ({ bookId, pageId }) => ({
        url: apiRoutesV2.teacherNotesByBook(bookId as string, pageId as string),
        method: 'GET',
      }),
      transformResponse: (response: { data: StudentGroup[] }) => {
        const formattedData = response.data?.map((group: any) => {
          return {
            groupId: group?.groupId,
            groupName: group?.groupName,
            students: group?.students?.map((student: any) => ({
              ...student,
            })),
          };
        });
        return formattedData;
      },
    }),
    getStudentTeacherNotes: builder.mutation<BlockNote[], getNotesParams>({
      query: ({ pageId, studentId }) => ({
        url: apiRoutesV2.teacherNotesByStudent(pageId as string, studentId as string),
        method: 'GET',
      }),
      transformResponse: (response: { data: BlockNote[] }) => {
        return response.data.map((note) => note);
      },
    }),
    downloadNotesPerStudent: builder.mutation<any, getNotesParams>({
      query: ({ bookId, pageId, studentId }) => ({
        url: apiRoutesV2.downloadNotesPerStudent(
          bookId as string,
          pageId as string,
          studentId as string
        ),
        responseType: 'blob',
        responseHandler: async (response) => {
          const student = store.getState().blockNotes.currentStudent;
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities - ${student?.group.groupName}_${student?.studentName}.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),

    downloadNotesPerTrajectory: builder.mutation<any, getNotesParams>({
      query: ({ bookId, trajectoryId }) => ({
        url: apiRoutesV2.downloadNotesPerTrajectory(bookId as string, trajectoryId as string),
        responseType: 'blob',
        responseHandler: async (response) => {
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),

    downloadNotesPerPage: builder.mutation<any, getNotesParams>({
      query: ({ bookId, pageId }) => ({
        url: apiRoutesV2.downloadNotesPerPage(bookId as string, pageId as string),
        responseType: 'blob',
        responseHandler: async (response) => {
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),

    downloadNotesPerGroup: builder.mutation<any, getNotesParams>({
      query: ({ bookId, pageId, group }) => ({
        url: apiRoutesV2.downloadNotesPerGroup(
          bookId as string,
          pageId as string,
          group?.groupId as string
        ),
        responseType: 'blob',
        responseHandler: async (response) => {
          const { currentGroup } = store.getState().blockNotes;
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities - ${currentGroup?.groupName}.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),

    downloadNotesPerGroupAndTrajectory: builder.mutation<any, getNotesParams>({
      query: ({ bookId, trajectoryId, group }) => ({
        url: apiRoutesV2.downloadNotesPerGroupAndTrajectory(
          bookId as string,
          trajectoryId as string,
          group?.groupId as string
        ),
        responseType: 'blob',
        responseHandler: async (response) => {
          const { currentGroup } = store.getState().blockNotes;
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities - ${currentGroup?.groupName}.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),

    downloadNotesPerStudentAndTrajectory: builder.mutation<any, getNotesParams>({
      query: ({ bookId, trajectoryId, studentId }) => ({
        url: apiRoutesV2.downloadNotesPerStudentAndTrajectory(
          bookId as string,
          trajectoryId as string,
          studentId as string
        ),
        responseType: 'blob',
        responseHandler: async (response) => {
          const { currentStudent } = store.getState().blockNotes;
          const download = window.URL.createObjectURL(await response.blob());
          const hiddenElement = document.createElement('a');
          hiddenElement.href = download;
          hiddenElement.target = '_blank';
          hiddenElement.download = `notities - ${currentStudent?.group.groupName}_${currentStudent?.studentName}.pdf`;
          hiddenElement.click();
          window.URL.revokeObjectURL(download);
          return { data: null };
        },
      }),
    }),
  }),
});

export const {
  useGetBlockNotesQuery: getBlockNotes,
  useCreateBlockNoteMutation,
  useDeleteBlockNoteMutation,
  usePatchBlockNoteMutation,
  useGetNotesByBookQuery,
  useGetStudentTeacherNotesMutation,
  useDownloadNotesPerStudentMutation,
  useDownloadNotesPerTrajectoryMutation,
  useDownloadNotesPerPageMutation,
  useDownloadNotesPerGroupMutation,
  useDownloadNotesPerGroupAndTrajectoryMutation,
  useDownloadNotesPerStudentAndTrajectoryMutation,
} = notesApi;
